import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function TabPanel({ children, value, index, variant, customSmallDimensions }) {
  const defaultSmallStyle = {
    p: 1,
    border: '1px solid #3a3a3a',
    borderRadius: '4px',
    backgroundColor: '#252526',
    color: '#fff',
    mt: 1,
    width: '100%',
    margin: '0px',
    boxSizing: 'border-box',
    minHeight: 'auto',
    overflow: 'visible',
  };

  const panelStyle =
    variant === 'small'
      ? {
          ...defaultSmallStyle,
          ...(customSmallDimensions || {}),
          display: 'flex',
          flexDirection: 'column',
          ...(customSmallDimensions?.overflow
            ? { overflow: customSmallDimensions.overflow }
            : { overflow: 'visible' }),
          '& .MuiGrid-container': {
            width: '100%',
            margin: 0,
            flexGrow: 1,
          },
          '& > div': {
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'visible',
          },
        }
      : {
          p: 2,
          border: '2px solid #3a3a3a',
          borderRadius: '8px',
          backgroundColor: '#252526',
          color: '#fff',
          mt: 0,
          width: '100%',
          margin: '8px',
          boxSizing: 'border-box',
          overflow: 'visible',
        };

  return (
    <div hidden={value !== index} style={{ height: '100%', width: '100%', overflow: 'hidden' }}>
      {value === index && 
        <Box sx={{
          ...panelStyle, 
          height: '100%', // Ensure panelStyle Box also takes full height
          display: 'flex', // Ensure children can take full height
          flexDirection: 'column' // Ensure children can take full height
        }}>
          {children}
        </Box>}
    </div>
  );
}

export default function TabView({
  tabLabels = [],
  tabContents = [],
  variant = 'normal',
  customSmallDimensions,
  disableUrlUpdate = false,
  initialActiveTab = '',
  smallTabColors = {},
  preventFlicker = false,
  // Additional style customization options
  containerStyle = {},
  tabHeadersStyle = {},
  tabContentStyle = {},
  customTabStyle = {},
  customActiveTabStyle = {},
  customInactiveTabStyle = {},
  onTabChange = () => {}, // Callback for tab changes
  activeTab = undefined, // For controlled component usage
}) {
  const query = useQuery();
  const navigate = useNavigate();

  const manualTabChange = useRef(false);
  const [initialized, setInitialized] = useState(false);

  // Support for controlled component usage
  useEffect(() => {
    if (activeTab !== undefined) {
      if (typeof activeTab === 'number' && activeTab >= 0 && activeTab < tabLabels.length) {
        setValue(tabLabels[activeTab].toLowerCase());
      } else if (typeof activeTab === 'string') {
        setValue(activeTab.toLowerCase());
      }
    }
  }, [activeTab, tabLabels]);

  const getInitialTab = () => {
    // If activeTab is provided, use it
    if (activeTab !== undefined) {
      if (typeof activeTab === 'number' && activeTab >= 0 && activeTab < tabLabels.length) {
        return tabLabels[activeTab].toLowerCase();
      } else if (typeof activeTab === 'string') {
        return activeTab.toLowerCase();
      }
    }
    
    return initialActiveTab
      ? initialActiveTab.toLowerCase()
      : query.get('tab') || (tabLabels.length > 0 ? tabLabels[0].toLowerCase() : '');
  };

  const getInitialSubTab = () => {
    // Check if initialActiveTab is provided for small tabs
    if (variant === 'small' && initialActiveTab) {
      return initialActiveTab.toLowerCase();
    }
    return query.get('subTab') || (tabLabels.length > 0 ? tabLabels[0].toLowerCase() : '');
  };

  const [value, setValue] = useState(getInitialTab());
  const [subValue, setSubValue] = useState(getInitialSubTab());

  useEffect(() => {
    setInitialized(true);
  }, []);

  useEffect(() => {
    if (initialized && initialActiveTab && !manualTabChange.current) {
      setValue(initialActiveTab.toLowerCase());
    }
  }, [initialActiveTab, initialized]);

  useEffect(() => {
    if (!initialized || disableUrlUpdate || manualTabChange.current) return;

    const tabParam = query.get('tab');
    if (tabParam && tabParam !== value) {
      setValue(tabParam);
    }
  }, [query, value, disableUrlUpdate, initialized]);

  const handleTabChange = (tabName) => {
    const lowerTabName = tabName.toLowerCase();
    if (value === lowerTabName) return;

    manualTabChange.current = true;
    setValue(lowerTabName);
    
    // Don't reset subValue here to keep active subtabs
    // setSubValue('');
    
    // Call the onTabChange callback
    const tabIndex = tabLabels.findIndex(label => label.toLowerCase() === lowerTabName);
    onTabChange(lowerTabName, tabIndex);

    if (!disableUrlUpdate) {
      const updatedParams = new URLSearchParams(window.location.search);
      updatedParams.set('tab', lowerTabName);
      // Don't delete subTab to keep URL parameters consistent
      // updatedParams.delete('subTab');
      navigate(`?${updatedParams.toString()}`);
      setTimeout(() => (manualTabChange.current = false), 200);
    } else {
      manualTabChange.current = false;
    }
  };

  const handleSubTabChange = (subTabName) => {
    const lowerSubTabName = subTabName.toLowerCase();
    if (subValue === lowerSubTabName) return;

    manualTabChange.current = true;
    setSubValue(lowerSubTabName);
    
    // Call the onTabChange callback
    onTabChange(lowerSubTabName);

    if (!disableUrlUpdate) {
      const updatedParams = new URLSearchParams(window.location.search);
      
      // Bevar hovedtab-parameter
      const currentTab = updatedParams.get('tab');
      if (currentTab) {
        updatedParams.set('tab', currentTab);
      }
      
      updatedParams.set('subTab', lowerSubTabName);
      navigate(`?${updatedParams.toString()}`);
      setTimeout(() => (manualTabChange.current = false), 200);
    } else {
      manualTabChange.current = false;
    }
  };

  // Default small tab colors
  const defaultSmallTabColors = {
    active: '#e9531f',
    inactive: '#69250d',
    activeHover: '#8c2e10',
    inactiveHover: '#a53713',
  };

  // Normal tab colors
  const defaultNormalTabColors = {
    active: '#1677ff',
    inactive: '#303031',
    activeHover: '#1677ff',
    inactiveHover: '#3b3b3b',
  };

  // Choose the appropriate color set based on variant
  const baseColors = variant === 'small' ? defaultSmallTabColors : defaultNormalTabColors;

  // Merge with any custom colors provided
  const tabColors = { ...baseColors, ...smallTabColors };

  // Define base styles for tabs based on variant
  const baseTabStyle = variant === 'small'
    ? {
        width: '140px',
        height: '40px',
        margin: '4px',
        borderRadius: '4px',
      }
    : {
        width: '180px',
        height: '80px',
        margin: '6px',
        marginBottom: '-15px',
        borderRadius: '8px',
      };

  // Merge base styles with any custom styles provided
  const tabStyle = { ...baseTabStyle, ...customTabStyle };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: 'calc(100% - 16px)', // Consider if this calc is still needed or should be 100%
        height: '100%', // Changed from 'auto' to '100%'
        overflow: 'hidden', // Changed from 'visible' to 'hidden'
        m: 0,
        p: variant === 'small' ? 1 : 2,
        boxSizing: 'border-box',
        ...containerStyle,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: variant === 'small' ? 1 : 2,
          mb: variant === 'small' ? 1 : 2,
          padding: '4px',
          boxSizing: 'border-box',
          // overflow: 'visible', // Removed, let parent handle overflow if needed
          ...tabHeadersStyle,
        }}
      >
        {tabLabels.map((label, index) => {
          const isSelected = (variant === 'small' ? subValue : value) === label.toLowerCase();
          
          // Apply appropriate styles based on selection state
          const activeStyle = {
            backgroundColor: tabColors.active,
            color: '#fff',
            fontWeight: 'bold',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            ...customActiveTabStyle,
          };
          
          const inactiveStyle = {
            backgroundColor: tabColors.inactive,
            color: '#c5c5c5',
            ...customInactiveTabStyle,
          };
          
          return (
            <Box
              key={`tab-${label}-${index}`}
              onClick={() =>
                variant === 'small' ? handleSubTabChange(label.toLowerCase()) : handleTabChange(label.toLowerCase())
              }
              sx={{
                ...tabStyle,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'background-color 0.2s, transform 0.1s, box-shadow 0.1s',
                ...(isSelected ? activeStyle : inactiveStyle),
                '&:hover': {
                  backgroundColor: isSelected ? tabColors.activeHover : tabColors.inactiveHover,
                  color: '#fff',
                  transform: 'scale(1.02)',
                  boxShadow: '0px 6px 15px rgba(0, 0, 0, 0.15)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }}
            >
              <Typography
                variant="body1"
                fontWeight={isSelected ? 'bold' : 'normal'}
              >
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          width: '100%',
          // height: 'auto', // flexGrow will handle height
          flexGrow: 1,
          overflow: 'hidden', // Changed from 'visible'
          boxSizing: 'border-box',
          display: 'flex', // Added to allow TabPanel to fill height
          flexDirection: 'column', // Added
          ...tabContentStyle,
        }}
      >
        {tabContents.map((content, index) => {
          const tabIndex = tabLabels[index].toLowerCase();
          const tabValue = variant === 'small' ? subValue : value;
          return (
            <TabPanel
              key={`panel-${tabIndex}`}
              value={tabValue}
              index={tabIndex}
              variant={variant}
              customSmallDimensions={customSmallDimensions}
            >
              {content}
            </TabPanel>
          );
        })}
      </Box>
    </Box>
  );
}
